import { api } from '../../../core/utils/apiClient';
import {
  EnrichmentSession,
  EnrichmentForm,
  FieldResponsesPayload,
  FieldSubmissionResult,
  EnrichmentAnalytics,
  FinalizeResult,
} from '../types';

class EnrichmentService {
  async startSession(companyId: number, questionCategories: string[]): Promise<EnrichmentSession> {
    const response = await api.post(
      `/api/v1/enrichment/companies/${companyId}/enrichment/start`,
      {
        question_categories: questionCategories,
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      
      // Handle 409 Conflict - session already exists
      if (response.status === 409) {
        const conflictError: any = {
          status: 409,
          message: error.error || error.detail || error.message || 'An enrichment session already exists for this company',
          session_id: error.active_session_id || error.session_id || error.sessionId || error.session?.session_id || null,
          company_id: error.company_id || null,
        };
        throw conflictError;
      }
      
      throw new Error(error.error || error.detail || error.message || 'Failed to start enrichment session');
    }

    return response.json();
  }

  async getSession(sessionId: string): Promise<EnrichmentSession> {
    const response = await api.get(`/api/v1/enrichment/sessions/${sessionId}`);
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new Error('SESSION_EXPIRED');
      }
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to load session');
    }
    return response.json();
  }

  /**
   * Poll a PROCESSING session until it transitions to ACTIVE or FAILED.
   * Returns the final session state.
   */
  async pollSessionUntilReady(
    sessionId: string,
    intervalMs = 3000,
    maxAttempts = 120,
    onPoll?: (session: EnrichmentSession) => void,
  ): Promise<EnrichmentSession> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const session = await this.getSession(sessionId);

      if (onPoll) onPoll(session);

      if (session.status !== 'processing') {
        return session;
      }

      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    throw new Error('Enrichment processing timed out. Please refresh and check the session status.');
  }

  async deleteSession(sessionId: string): Promise<void> {
    const response = await api.delete(`/api/v1/enrichment/sessions/${sessionId}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to cancel session');
    }
  }

  async skipSession(sessionId: string): Promise<EnrichmentSession> {
    const response = await api.post(`/api/v1/enrichment/sessions/${sessionId}/skip`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to skip enrichment');
    }
    return response.json();
  }

  async finalizeSession(sessionId: string): Promise<FinalizeResult> {
    const response = await api.post(`/api/v1/enrichment/sessions/${sessionId}/finalize`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to finalize session');
    }
    return response.json();
  }

  async getForm(sessionId: string): Promise<EnrichmentForm> {
    const response = await api.get(`/api/v1/enrichment/sessions/${sessionId}/form`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to fetch form data');
    }
    return response.json();
  }

  async submitResponses(
    sessionId: string,
    payload: FieldResponsesPayload
  ): Promise<FieldSubmissionResult> {
    const response = await api.post(
      `/api/v1/enrichment/sessions/${sessionId}/responses`,
      payload
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to submit responses');
    }
    return response.json();
  }

  async getAnalytics(sessionId: string): Promise<EnrichmentAnalytics> {
    const response = await api.get(`/api/v1/enrichment/sessions/${sessionId}/analytics`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to load analytics');
    }
    return response.json();
  }

  async getActiveSession(companyId: number): Promise<{
    active: boolean;
    session_id?: string;
    status?: string;
    current_score?: number;
    created_at?: string;
  }> {
    const response = await api.get(`/api/v1/enrichment/companies/${companyId}/active-session`);
    if (!response.ok) {
      return { active: false };
    }
    return response.json();
  }

  async exportSession(sessionId: string): Promise<Blob> {
    const response = await api.get(`/api/v1/enrichment/sessions/${sessionId}/export`, {
      headers: {
        Accept: 'application/octet-stream',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to export session');
    }

    return response.blob();
  }
}

export const enrichmentService = new EnrichmentService();

